/**
 * Dependency Graph Builder
 *
 * Builds a graph structure from tasks for layout calculations.
 *
 * ENHANCED: Detects back-edges (edges that would create cycles) and separates them.
 * This allows the layout algorithm to:
 * 1. Use the longest path for layer assignment (ignoring back-edges)
 * 2. Still render back-edges as special "feedback" connections
 *
 * NESTED FEEDBACK LOOPS:
 * - Level 1: Back-edge STARTS from a node branching off Critical Path
 * - Level N: Back-edge STARTS from a Level N-1 feedback node
 * - The TARGET of a back-edge can be any node at level <= source level
 */

import { getAllConditionTaskIds } from '../../shared/ConditionsDropdown/utils/conditionHelpers';
import { detectBackEdgesAndFeedbackNodes_phase1, detectNestedBackEdges } from './backEdgeDetection';
import { assignNestingLevels } from './nestingLevelAssignment';
import { getNoteAnchor, isNoteNode } from '../utils/noteUtils';

/**
 * Helper to extract taskId from dependency (handles both string and object format)
 */
export function getDependencyTaskId(dep) {
  if (typeof dep === 'string') return dep;
  return dep?.taskId || null;
}

/**
 * Get all dependency task IDs for a task.
 * Uses unlockConditions if present (v1 or v2), otherwise falls back to dependsOn.
 * Returns all task IDs from all blocks (for layout, we need all connections).
 */
export function getEffectiveDependencyIds(task, allTasks = null) {
  const ids = getAllConditionTaskIds(task.unlockConditions, allTasks);
  if (ids.length > 0) return ids;
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  return deps.map(dep => getDependencyTaskId(dep)).filter(Boolean);
}

/**
 * Orchestrates back-edge detection, feedback node identification, nested loop
 * detection, and nesting level assignment.
 */
function detectBackEdgesAndFeedbackNodes(tasks, childrenMap, parentsMap) {
  const { backEdges, backEdgeList, feedbackNodes } =
    detectBackEdgesAndFeedbackNodes_phase1(tasks, childrenMap, parentsMap);

  detectNestedBackEdges(backEdgeList, backEdges, feedbackNodes, childrenMap, parentsMap);

  const { nodeLevels, backEdgesWithLevels } =
    assignNestingLevels(tasks, backEdgeList, backEdges, feedbackNodes, parentsMap, childrenMap);

  return { backEdges, feedbackNodes, nodeLevels, backEdgesWithLevels };
}

/**
 * Build a dependency graph from tasks.
 * Milestones are passive pass-through layout nodes; mission dependencies are unchanged.
 */
export function buildDependencyGraph(tasks) {
  const graph = {
    nodes: new Map(),
    edges: [],
    backEdges: [],
    forwardEdges: [],
    inDegree: new Map(),
    outDegree: new Map(),
    children: new Map(),
    parents: new Map(),
    allChildren: new Map(),
    allParents: new Map(),
  };

  tasks.forEach(task => {
    graph.nodes.set(task.id, task);
    graph.inDegree.set(task.id, 0);
    graph.outDegree.set(task.id, 0);
    graph.children.set(task.id, []);
    graph.parents.set(task.id, []);
    graph.allChildren.set(task.id, []);
    graph.allParents.set(task.id, []);
  });

  const linkDependency = (depId, taskId, childrenMap, parentsMap) => {
    if (!depId || !graph.nodes.has(depId)) return;
    const depTask = graph.nodes.get(depId);
    if (depTask?.nodeType === 'milestone' || depTask?.nodeType === 'note') return;
    childrenMap.get(depId).push(taskId);
    parentsMap.get(taskId).push(depId);
  };

  // First pass: build temporary maps for back-edge detection
  const tempChildrenMap = new Map();
  const tempParentsMap = new Map();
  tasks.forEach(task => {
    tempChildrenMap.set(task.id, []);
    tempParentsMap.set(task.id, []);
  });
  tasks.forEach(task => {
    if (task.nodeType === 'note') return;
    getEffectiveDependencyIds(task, tasks).forEach((depId) => {
      linkDependency(depId, task.id, tempChildrenMap, tempParentsMap);
    });
  });

  const { backEdges: backEdgeSet, feedbackNodes, nodeLevels, backEdgesWithLevels } =
    detectBackEdgesAndFeedbackNodes(tasks, tempChildrenMap, tempParentsMap);

  graph.feedbackNodes = feedbackNodes;
  graph.nodeLevels = nodeLevels;
  graph.backEdgesWithLevels = backEdgesWithLevels;

  // Second pass: populate graph, separating forward and back edges
  tasks.forEach(task => {
    if (task.nodeType === 'note') return;
    getEffectiveDependencyIds(task, tasks).forEach((depId) => {
      if (!depId || !graph.nodes.has(depId)) return;
      const depTask = graph.nodes.get(depId);
      if (depTask?.nodeType === 'milestone' || depTask?.nodeType === 'note') return;

      const edgeKey = `${depId}->${task.id}`;
      const edge = { from: depId, to: task.id };
      const isBackEdge = backEdgeSet.has(edgeKey);

      graph.edges.push({ ...edge, isBackEdge });
      graph.allChildren.get(depId).push(task.id);
      graph.allParents.get(task.id).push(depId);

      if (isBackEdge) {
        graph.backEdges.push(edge);
      } else {
        graph.forwardEdges.push(edge);
        graph.inDegree.set(task.id, (graph.inDegree.get(task.id) || 0) + 1);
        graph.outDegree.set(depId, (graph.outDegree.get(depId) || 0) + 1);
        graph.children.get(depId).push(task.id);
        graph.parents.get(task.id).push(depId);
      }
    });
  });

  // Notes occupy a real layout slot (same spacing as missions). A host→note
  // forward edge puts them in the next layer so they cannot sit on siblings.
  attachNoteLayoutEdges(graph, tasks);

  return graph;
}

function attachNoteLayoutEdges(graph, tasks) {
  (tasks || []).forEach((task) => {
    if (!isNoteNode(task)) return;
    const hostTaskId = getNoteAnchor(task)?.hostTaskId;
    if (!hostTaskId || !graph.nodes.has(hostTaskId)) return;
    if ((graph.parents.get(task.id) || []).includes(hostTaskId)) return;

    const edge = { from: hostTaskId, to: task.id };
    graph.edges.push({ ...edge, isBackEdge: false });
    graph.forwardEdges.push(edge);
    graph.allChildren.get(hostTaskId).push(task.id);
    graph.allParents.get(task.id).push(hostTaskId);
    graph.children.get(hostTaskId).push(task.id);
    graph.parents.get(task.id).push(hostTaskId);
    graph.inDegree.set(task.id, (graph.inDegree.get(task.id) || 0) + 1);
    graph.outDegree.set(hostTaskId, (graph.outDegree.get(hostTaskId) || 0) + 1);
  });
}
